import React, { createContext, useContext, useState, useEffect } from 'react';
import { FirebaseService } from 'services/firebaseService';
import { AuthService } from 'services/authService';
import { User } from 'firebase/auth';
import { AnalyticsService } from 'services/firebaseService';
import { Meal, UserProfile } from '@llm-macro-tracker/shared';

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  addMeal: (meal: Omit<Meal, 'userId' | 'timestamp' | 'v'>) => Promise<string>;
  getMealsForDate: (date: Date) => Promise<(Meal & { id: string })[]>;
  deleteMeal: (mealId: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateMeal: (
    mealId: string,
    updatedMeal: Omit<Meal, 'userId' | 'timestamp' | 'v'>,
  ) => Promise<string>;
  userProfile: UserProfile | null;
  updateUserProfile: (profile: UserProfile) => Promise<void>;
  getUserProfile: (userId: string) => Promise<UserProfile | null>;
  uploadImage: (file: File) => Promise<string>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged(async (user) => {
      setUser(user);
      if (user) {
        try {
          const profile = await FirebaseService.getUserProfile(user.uid);
          if (profile) {
            setUserProfile(profile);
          } else {
            // Set default profile for new users
            const defaultProfile: UserProfile = {
              v: 1,
              dailyCalorieGoal: 2000,
              macroGoals: { protein: 30, carbs: 40, fat: 30 },
              activityLevel: 'moderate',
              weightGoal: 'maintain',
              weight: null,
              height: null,
              age: 30,
              gender: 'male',
            };
            await FirebaseService.updateUserProfile(user.uid, defaultProfile);
            setUserProfile(defaultProfile);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Set a default profile even if there's an error
          setUserProfile({
            v: 1,
            dailyCalorieGoal: 2000,
            macroGoals: { protein: 30, carbs: 40, fat: 30 },
            activityLevel: 'moderate',
            weightGoal: 'maintain',
            weight: null,
            height: null,
            age: 30,
            gender: 'male',
          });
        }
      } else {
        setUserProfile(null);
      }
      setIsLoading(false);
      AnalyticsService.setUserId(user);
      if (user) {
        AnalyticsService.setUserProperties({
          accountCreationDate: user.metadata.creationTime,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const addMeal = async (meal: Omit<Meal, 'userId' | 'timestamp' | 'v'>): Promise<string> => {
    if (!user) throw new Error('User not logged in');
    return FirebaseService.addMeal(user.uid, meal);
  };

  const getMealsForDate = async (date: Date) => {
    if (!user) throw new Error('User not logged in');
    return FirebaseService.getMealsForDate(user.uid, date);
  };

  const deleteMeal = async (mealId: string) => {
    if (!user) throw new Error('User not logged in');
    return FirebaseService.deleteMeal(user.uid, mealId);
  };

  const updateMeal = async (
    mealId: string,
    updatedMeal: Omit<Meal, 'userId' | 'timestamp' | 'v'>,
  ) => {
    if (!user) throw new Error('User not logged in');
    return FirebaseService.updateMeal(user.uid, mealId, updatedMeal);
  };

  const register = async (email: string, password: string) => {
    await AuthService.register(email, password);
  };

  const login = async (email: string, password: string) => {
    await AuthService.login(email, password);
  };

  const logout = async () => {
    await AuthService.logout();
  };

  const updateUserProfile = async (profile: UserProfile) => {
    if (!user) throw new Error('User not logged in');
    try {
      await FirebaseService.updateUserProfile(user.uid, profile);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      return await FirebaseService.getUserProfile(userId);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    if (!user) throw new Error('User not logged in');
    return FirebaseService.uploadImage(user.uid, file);
  };

  const contextValue: UserContextType = {
    user,
    isLoading,
    addMeal,
    getMealsForDate,
    deleteMeal,
    register,
    login,
    logout,
    updateMeal,
    userProfile,
    updateUserProfile,
    getUserProfile,
    uploadImage,
  };

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export default UserProvider;
